<script lang="ts">
	import { Background } from "@components/Background.js";
	import BookMeeting from "@components/BookMeeting.svelte";
	import FaqItems from "@components/FaqItems.svelte";
	import InfoCard from "@components/InfoCard.svelte";
	import Ol from "@components/Ol.svelte";
	import { OlSize } from "@components/OlProps.js";
	import Pill from "@components/Pill.svelte";
	import { PillColor, PillSize } from "@components/PillProps.js";
	import ReferenceContents from "@components/ReferenceContents.svelte";
	import Section from "@components/Section.svelte";
	import TechnologyCards from "@components/TechnologyCards.svelte";
	import Formatted from "@components/common/Formatted.svelte";
	import Icon from "@components/common/Icon.svelte";
	import Link from "@components/common/Link.svelte";
	import { ButtonVariant } from "@components/common/getButtonClass.js";
	import Layout from "@components/layout/Layout.svelte";
	import arrowNext from "@core/assets/icons/arrow-next.svg?raw";
	import brackets from "@core/assets/icons/brackets.svg?raw";
	import { getClientsPath } from "@core/schema/paths/getClientsPath.js";
	import { getConnectWithFastestPath } from "@core/schema/paths/getConnectWithFastestPath.js";
	import { getDevelopmentPath } from "@core/schema/paths/getDevelopmentPath.js";
	import { getSolutionsPath } from "@core/schema/paths/getSolutionsPath.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import { BookMeetingPerson, getBookMeetingPeople } from "../data/getBookMeetingPeople.js";
	import { getFaqsDigi } from "../data/getFaqsDigi.js";
	import { getHowWeDigi } from "../data/getHowWeDigi.js";
	import { getReasonsDigi } from "../data/getReasonsDigi.js";
	import { ReferencedClients } from "../data/getReferences.js";
	import { getServicesDigi } from "../data/getServicesDigi.js";
	import { getTechnologies } from "../data/getTechnologies.js";
	import type { DigitalizationAndAutomationProps } from "./DigitalizationAndAutomationProps.js";

	const { tenant } = getGlobalTemplateProps();

	const _: DigitalizationAndAutomationProps = $props();
	const digiSoftwareForFields = [
		"Dokumenty a smlouvy",
		"Účetnictví a fakturace",
		"Schvalovací procesy",
		"Docházka a evidence práce",
		"Mzdová agenda",
		"Objednávky a logistika",
		"Zákaznická podpora",
		"Nábor zaměstnanců",
		"Zásobování a nákup",
		"Správa firemního majetku",
		"Reklamace a servis",
		"Pracovní postupy a směrnice",
	];

	const digiProcesses = [
		"Fakturace",
		"E-mailové a jiné notifikace",
		"Mzdová agenda",
		"Marketingové kampaně",
		"Zpracování dokumentů",
		"Plánování",
		"Zpětná vazba od zákazníků",
		"Řízení projektů",
		"Objednávkový proces",
		"Personalizace obsahu ",
	];
</script>

<Layout withStructuredData>
	<Section background={Background.LightGradient} centered gridHeadline="container-6" gridDesc="container-6" hero>
		{#snippet headline()}
			<Formatted text="Digitalizací a automatizací firmám pomáháme cg[[nezůstat pozadu]]" />
		{/snippet}
		{#snippet desc()}
			<p>
				<Formatted
					text="Stále spoléháte primárně na papír, tužku a excelové tabulky? Povolejte nás do akce – pomocí digitalizace a automatizace firemních procesů sp[[váš podnik zbavíme zbytečných nákladů, rutinní práce i otravné administrativy.]]"
				/>
			</p>
		{/snippet}
		{#snippet cta()}
			<Link
				variant={ButtonVariant.PhloxOutlineLightBg}
				class="flex items-center justify-center mx-auto mt-2"
				href={getConnectWithFastestPath(tenant)}
			>
				<Formatted text="Chci fastest služby" />
				<Icon icon={arrowNext} class="ml-2.5 text-fstgrey-900 w-5"></Icon>
			</Link>
		{/snippet}
	</Section>

	<Section
		gridHeadline="container-12 md:container-8 xl:container-start-2 xl:container-end-5"
		gridDesc="container-12 md:container-8 xl:container-start-7 xl:container-end-10"
	>
		{#snippet headline()}
			<Formatted text="Ukážeme vám, že digitalizace a automatizace cg[[nemusí být peklo]]" />
		{/snippet}
		{#snippet desc()}
			<p>
				<Formatted
					text="Odkládáte modernizaci firmy jen kvůli obavám z náročnosti digitalizace a automatizace? Už nemusíte – sp[[celým procesem vás provedeme od začátku až do konce.]]"
				/>
			</p>
			<p>
				<Formatted
					text="Zanalyzujeme vaši situaci, navrhneme ideální [{getSolutionsPath(
						tenant,
					)}][[řešení]] a [{getDevelopmentPath(
						tenant,
					)}][[vyvineme]] pro vás potřebné nástroje a aplikace na míru. Když budete chtít, rovnou se pustíme do automatizace některých digitálních procesů. sp[[Digitalizace a automatizace pro nás nejsou jen vágní slova, ale způsob, jak firmám ušetřit čas i peníze.]]"
				/>
			</p>
		{/snippet}
		<Link
			variant={ButtonVariant.Phlox}
			class="flex items-center justify-center mx-auto mt-8 lg:mt-12"
			href={getConnectWithFastestPath(tenant)}
		>
			<Formatted text="Nezávazně poptat" />
			<Icon icon={arrowNext} class="ml-2.5 text-fstgrey-900 w-5"></Icon>
		</Link>
	</Section>

	<Section
		paddingFollowNext
		gridHeadline="container-12 md:container-8 xl:container-start-2 xl:container-end-5"
		gridDesc="container-12 md:container-8 xl:container-start-7 xl:container-end-10"
		gridChildren="container-12"
	>
		{#snippet headline()}
			<Formatted text="cg[[6 důvodů]], proč se pustit do digitalizace a automatizace" />
		{/snippet}
		{#snippet desc()}
			<div class="flex flex-col gap-8">
				<Ol items={getReasonsDigi(tenant)} size={OlSize.Medium} />
			</div>
		{/snippet}

		<Link
			variant={ButtonVariant.Phlox}
			class="mx-auto flex items-center justify-center mt-8 lg:mt-12"
			href={getConnectWithFastestPath(tenant)}
			>Chci fastest služby
			<Icon icon={arrowNext} class="ml-2 text-fstgrey-900"></Icon>
		</Link>
	</Section>

	<Section
		gridHeadline="container-12 md:container-8 xl:container-start-2 xl:container-end-5"
		gridChildren="container-12 md:container-8 xl:container-start-7 xl:container-end-11"
	>
		{#snippet headline()}
			<Formatted text="Jaké cg[[funkce]] můžeme do webové aplikace cg[[zahrnout]]" />
		{/snippet}
		<div class="flex flex-wrap gap-1 lg:gap-2">
			{#each digiSoftwareForFields as fnct (fnct)}
				<Pill size={PillSize.Medium} color={PillColor.White}>
					<Icon icon={brackets} square class="text-fstgreen h-5"></Icon>
					<span>{fnct}</span>
				</Pill>
			{/each}
		</div>
	</Section>

	<Section background={Background.LightGradient} noPadding="bot" gridHeadline="container-8 lg:text-center">
		{#snippet headline()}
			<!-- <Formatted text="Pomocí kterých řešení firmy cg[[digitalizujeme a automatizujeme]]?" /> TODO: Overflows -->
			<Formatted text="Pomocí kterých řešení firmy cg[[digitalizujeme?]]" />
		{/snippet}
	</Section>
	<Section gridChildren="max-md:full-width" background={Background.White} noPadding="both">
		<div
			class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1 max-lg:full-width lg:px-1 bg-fstlight-100 border-t-4 border-fstlight-100"
		>
			{#each getServicesDigi(tenant) as { title, desc }, index (index)}
				<InfoCard {title} {desc} />
			{/each}
		</div>
	</Section>
	<Link
		variant={ButtonVariant.Phlox}
		class="flex items-center justify-center mx-auto mt-8 lg:mt-12"
		href={getConnectWithFastestPath(tenant)}
	>
		<Formatted text="Chci nezávaznou konzultaci" />
		<Icon icon={arrowNext} class="ml-2.5 text-fstgrey-900 w-5"></Icon>
	</Link>

	<Section
		gridHeadline="container-12 md:container-8 xl:container-start-2 xl:container-end-5"
		gridDesc="container-12 md:container-8 xl:container-start-7 xl:container-end-11"
	>
		{#snippet headline()}
			<Formatted text="Dokážeme cg[[zautomatizovat]] desítky rutinních procesů" />
		{/snippet}
		{#snippet desc()}
			<p>
				<Formatted
					text="Digitalizace bývá prvním krokem na cestě k efektivnějšímu podnikáním. Tím druhým a neméně zásadním je automatizace manuálních a jinak rutinních procesů. sp[[Co vše pro pro vás zautomatizujeme?]]"
				/>
			</p>
			<div class="flex flex-wrap gap-1 lg:gap-2">
				{#each digiProcesses as fnct (fnct)}
					<Pill size={PillSize.Small} color={PillColor.White}>
						<Icon icon={brackets} square class="text-fstgreen h-5"></Icon>
						<span>{fnct}</span>
					</Pill>
				{/each}
			</div>
		{/snippet}
	</Section>

	<Section
		paddingFollowNext
		gridHeadline="container-12 md:container-8 xl:container-start-2 xl:container-end-5"
		gridDesc="container-12 md:container-8 xl:container-start-7 xl:container-end-10"
		gridChildren="container-12"
	>
		{#snippet headline()}
			<Formatted text="cg[[Jak]] při digitalizaci a automatizaci cg[[postupujeme]]" />
		{/snippet}
		{#snippet desc()}
			<div class="flex flex-col gap-8">
				<Ol items={getHowWeDigi(tenant)} size={OlSize.Medium} />
			</div>
		{/snippet}

		<Link
			variant={ButtonVariant.Phlox}
			class="mx-auto flex items-center justify-center mt-8 lg:mt-12"
			href={getConnectWithFastestPath(tenant)}
			>Chci fastest služby
			<Icon icon={arrowNext} class="ml-2 text-fstgrey-900"></Icon>
		</Link>
	</Section>
	<Section gridHeadline="container-8" centered>
		{#snippet headline()}
			<Formatted text="cg[[Komu]] jsme digitalizací a automatizací cg[[zrychlili]] růst podnikání?" />
		{/snippet}

		<div class="mt-12">
			<ReferenceContents
				clients={[ReferencedClients.Tymbe, ReferencedClients.SladkaDilna, ReferencedClients.OxPoint]}
				descriptionKey="desc"
			/>
		</div>
		<Link variant={ButtonVariant.Phlox} class="mx-auto flex items-center justify-center" href={getClientsPath(tenant)}
			>Další ukázky práce</Link
		>
	</Section>

	<Section
		centered
		gridHeadline="container-6"
		gridDesc="container-6"
		background={Background.LightGradient}
		paddingFollowNext
	>
		{#snippet headline()}
			<Formatted text="Spoléháme na moderní technologie a vyvíjíme cg[[na cloudu]]" />
		{/snippet}
		{#snippet desc()}
			<p>
				<Formatted text="Pracujeme s ověřenými technologiemi, které nabízí ideální poměr sp[[cena/výkon.]]" />
			</p>
		{/snippet}
	</Section>
	<TechnologyCards technologies={getTechnologies(tenant)} />
	<Link
		variant={ButtonVariant.Phlox}
		class="mx-auto flex items-center justify-center mt-8 lg:mt-12"
		href={getConnectWithFastestPath(tenant)}
		>Chci fastest služby
		<Icon icon={arrowNext} class="ml-2 text-fstgrey-900"></Icon>
	</Link>

	<Section centered gridHeadline="container-6">
		{#snippet headline()}
			<Formatted text="Často kladené otázky k cg[[digitalizaci a automatizaci]]" />
		{/snippet}
		<FaqItems faqs={getFaqsDigi(tenant)} />
	</Section>

	{@const contacts = getBookMeetingPeople(tenant)}
	<BookMeeting contacts={[contacts[BookMeetingPerson.Kucera]]} />
</Layout>
